import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { Send, Loader2, Package, MapPin, DollarSign } from 'lucide-react';

const countries = [
  'Nigeria', 'Ghana', 'Kenya', 'South Africa', 'Egypt', 'Morocco', 'Tanzania',
  'Ethiopia', 'Rwanda', 'Senegal', "Côte d'Ivoire", 'Cameroon', 'Uganda',
  'Zimbabwe', 'Botswana', 'Namibia', 'Tunisia', 'Algeria', 'Angola', 'Benin',
  'United States', 'United Kingdom', 'Germany', 'France', 'China', 'India', 'UAE', 'Other'
];

const deliveryTimelines = [
  { value: 'urgent', label: 'Urgent (1-2 weeks)' },
  { value: 'standard', label: 'Standard (2-4 weeks)' },
  { value: 'flexible', label: 'Flexible (1-2 months)' },
  { value: 'negotiable', label: 'Negotiable' }
];

const paymentTerms = [
  { value: 'advance', label: '100% Advance' },
  { value: 'partial', label: 'Partial Advance (30-50%)' },
  { value: 'lc', label: 'Letter of Credit' },
  { value: 'escrow', label: 'Escrow' },
  { value: 'negotiable', label: 'Negotiable' }
];

const InquiryModal = ({ open, onClose, product, seller }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    quantity_needed: product?.moq || 1,
    target_price: '',
    delivery_country: '',
    delivery_timeline: 'standard',
    payment_terms_preferred: 'negotiable',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async () => {
    if (!formData.quantity_needed || !formData.delivery_country || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('atff_token');
      await axios.post(`${API}/inquiries`, {
        product_id: product.product_id,
        seller_id: seller.user_id || product.seller_id,
        quantity_needed: parseInt(formData.quantity_needed),
        target_price: formData.target_price ? parseFloat(formData.target_price) : null,
        delivery_country: formData.delivery_country,
        delivery_timeline: formData.delivery_timeline,
        message: formData.message,
        payment_terms_preferred: formData.payment_terms_preferred
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Inquiry sent successfully! The seller will be notified.');
      onClose();
      // Reset form
      setFormData({
        quantity_needed: product?.moq || 1,
        target_price: '',
        delivery_country: '',
        delivery_timeline: 'standard',
        payment_terms_preferred: 'negotiable',
        message: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send inquiry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-600" />
            Send Inquiry
          </DialogTitle>
          <DialogDescription>
            Contact the seller about this product
          </DialogDescription>
        </DialogHeader>

        {/* Product Summary */}
        <div className="p-3 bg-gray-50 rounded-lg mb-4">
          <div className="flex gap-3">
            {product?.images?.[0] && (
              <img
                src={product.images[0]}
                alt={product.name}
                className="w-16 h-16 object-cover rounded"
              />
            )}
            <div className="flex-1">
              <h4 className="font-medium text-charcoal">{product?.name}</h4>
              <p className="text-sm text-gray-500">by {seller?.name || product?.seller_name}</p>
              <p className="text-sm font-medium text-continental">
                {product?.currency} {product?.price?.toLocaleString()} / unit
              </p>
              <p className="text-xs text-gray-400">MOQ: {product?.moq} units</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quantity_needed">Quantity Needed *</Label>
              <Input
                id="quantity_needed"
                name="quantity_needed"
                type="number"
                min={product?.moq || 1}
                value={formData.quantity_needed}
                onChange={handleChange}
                placeholder={`Min: ${product?.moq || 1}`}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="target_price">Target Price (Optional)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="target_price"
                  name="target_price"
                  type="number"
                  step="0.01"
                  value={formData.target_price}
                  onChange={handleChange}
                  placeholder="Per unit"
                  className="pl-8"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Delivery Country *</Label>
            <Select
              value={formData.delivery_country}
              onValueChange={(v) => handleSelectChange('delivery_country', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Where should it be delivered?" />
              </SelectTrigger>
              <SelectContent>
                {countries.map(country => (
                  <SelectItem key={country} value={country}>{country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Delivery Timeline</Label>
              <Select
                value={formData.delivery_timeline}
                onValueChange={(v) => handleSelectChange('delivery_timeline', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {deliveryTimelines.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Payment Terms</Label>
              <Select
                value={formData.payment_terms_preferred}
                onValueChange={(v) => handleSelectChange('payment_terms_preferred', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {paymentTerms.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Your Message *</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Introduce yourself and explain your requirements..."
              rows={4}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Inquiry
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InquiryModal;
